import { Component, DestroyRef, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MetadataService } from '../../../../services/metadata.service';
import { catchError, combineLatest, filter, map, NEVER, startWith, Subject, switchMap, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomFieldType, MetadataColumn } from '../../../../models/metadata';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatHint } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ShipmentDetailsService } from '../../services/shipment-details.service';
import { openConfirmationDialog } from '../../../confirmation-popup/confirmation-popup';
import { CustomFieldTypePipe } from '../../pipes/custom-field-type.pipe';
import { UserService } from '../../../../services/user.service';
import { CustomFieldsSkeletonComponent } from './custom-fields-skeleton/custom-fields-skeleton.component';
import { CustomFieldDropdownComponent } from './custom-field-dropdown/custom-field-dropdown.component';
import { CustomFieldTextareaComponent } from './custom-field-textarea/custom-field-textarea.component';
import { CustomFieldTextComponent } from './custom-field-text/custom-field-text.component';
import { CustomFieldDatepickerComponent } from './custom-field-datepicker/custom-field-datepicker.component';
import { CustomFieldNumberComponent } from './custom-field-number/custom-field-number.component';
import { Shipment } from '../../../../pages/overview/models/shipment';
import { ContainerDetailsService } from '../../../../pages/containers/services/container-details.service';
import { Container } from '../../../../pages/overview/models/container';
import { MatIcon } from '@angular/material/icon';
import { GoogleTagManagerService } from '../../../../services/google-tag-manager.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorHandlerService } from '../../../../utils/cross-xss-script-error-handler-service';

interface MappedMetadataColumn extends MetadataColumn {
  selected: boolean;
}

@Component({
  selector: 'app-custom-fields',
  standalone: true,
  templateUrl: './custom-fields.component.html',
  imports: [
    MatButton,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    ReactiveFormsModule,
    MatHint,
    CustomFieldTypePipe,
    CustomFieldsSkeletonComponent,
    CustomFieldDropdownComponent,
    CustomFieldTextareaComponent,
    CustomFieldTextComponent,
    CustomFieldDatepickerComponent,
    CustomFieldNumberComponent,
    MatIcon
  ],
  styleUrl: './custom-fields.component.scss'
})
export class CustomFieldsComponent implements OnChanges {
  @Input() item: Shipment | Container | null = null;
  @Input() openedFrom: 'Shipment' | 'Container' = 'Shipment';

  allAvailableOptions: MappedMetadataColumn[] = [];
  form = new FormArray<FormGroup>([]);
  maxNumberOfFields: number = 0;
  saving = false;
  isUserInternal = false;

  private refresh$ = new Subject<void>();
  private gtmService = inject(GoogleTagManagerService);
  private destroyRef = inject(DestroyRef);
  private metadataService = inject(MetadataService);
  private shipmentDetailsService = inject(ShipmentDetailsService);
  private containerDetailsService = inject(ContainerDetailsService);
  private userService = inject(UserService);
  private matSnackBar = inject(MatSnackBar);
  private matDialog = inject(MatDialog);
  private errorHandler = inject(ErrorHandlerService);
  ngOnChanges(changes: SimpleChanges) {
    if (changes['item'] && changes['item'].currentValue) {
      this.refresh$
        .pipe(
          startWith(null),
          switchMap(() => this.initializeFormFields()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  delete(index: number) {
    if (this.form.at(index).get('name')?.value) {
      openConfirmationDialog(this.matDialog, {
        title: 'Delete custom field',
        message: 'Are you sure you want to delete this custom field?'
      })
        .afterClosed()
        .pipe(
          filter(result => result === 'Confirmed'),
          tap(() => this.removeAt(index)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.removeAt(index);
    }
  }

  discardChanges() {
    openConfirmationDialog(this.matDialog, {
      title: 'Discard changes',
      message: 'Are you sure you want to discard all changes?'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        tap(() => this.refresh$.next()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  save() {
    this.saving = true;
    const source =
      this.openedFrom === 'Shipment'
        ? this.shipmentDetailsService.updateCustomFields(this.item!.shipmentNo, this.mappedFormValues())
        : this.containerDetailsService.updateCustomFields(
            {
              containerNo: (this.item as Container).containerNo,
              masterNo: (this.item as Container).masterNo,
              customerNo: (this.item as Container).customerNo
            },
            this.mappedFormValues()
          );
    source
      .pipe(
        tap(() => {
          this.saving = false;
          this.matSnackBar.open('Custom fields updated successfully.', 'x', { panelClass: 'snackbar-green' });
        }),
        catchError(error => {
          console.error(error);
          this.saving = false;
          this.errorHandler.showApiError(
            error,
            'Could not update custom fields.',
            this.allAvailableOptions,
            (fieldName, options) => options.find(x => x.key === fieldName)?.name,
            true
          );
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.gtmService.pushEvent({
      event: this.openedFrom === 'Shipment' ? 'shipment_details' : 'container_details',
      type: { tabs_interacted: 'Custom Fields Saved' }
    });
  }

  addGroup(key: string | null = null, column: MetadataColumn | null = null) {
    const group = new FormGroup({
      name: new FormControl(key),
      value: new FormControl(key ? this.item?.customFields[key] : null),
      dataType: new FormControl(column ? column.dataType : null),
      customFieldType: new FormControl(column ? column.customFieldType! : null),
      validators: new FormControl(column ? column.customFieldValidators : null),
      allowedValues: new FormControl(column ? column.allowedValues : null)
    });
    this.form.push(group);
    if (this.shouldDisableGroup(column?.customFieldType)) {
      group.get('value')?.disable();
    }
    this.subscribeToFieldChange(group);
  }

  private mappedFormValues() {
    return this.form
      .getRawValue()
      .reduce((acc, { name, value }) => (name && value !== null ? { ...acc, [name]: value } : acc), {});
  }

  private removeAt(index: number) {
    this.form.removeAt(index);
    this.refreshSelection();
  }

  private formArrayInit(shipmentColumns: MetadataColumn[]) {
    this.form.clear();
    shipmentColumns.forEach(column => {
      const key = column.accessPath.split('.')[1];
      if (this.shouldShowCustomField(key, column)) {
        this.addGroup(key, column);
      }
    });
  }

  private shouldShowCustomField(key: string, column: MetadataColumn) {
    return this.item?.customFields[key] && (this.isUserInternal || column.customFieldType !== 'Internal');
  }

  private setAllAvailableOptions(shipmentColumns: MetadataColumn[]) {
    this.allAvailableOptions = shipmentColumns
      .map(({ accessPath, ...rest }) => {
        const splitName = accessPath.split('.')[1];
        const isSelected = this.form.controls
          .find(control => control.get('name')?.value === splitName)
          ?.get('value')?.value;
        return { ...rest, accessPath, key: splitName, selected: isSelected };
      })
      .sort((a, b) => {
        const aType = a.customFieldType === 'Internal' ? 1 : 0;
        const bType = b.customFieldType === 'Internal' ? 1 : 0;

        if (aType !== bType) {
          return aType - bType;
        }

        return a.order - b.order;
      });
  }

  private subscribeToFieldChange(group: FormGroup) {
    group
      .get('name')
      ?.valueChanges.pipe(
        tap(name => {
          const newField = this.allAvailableOptions.find(option => option.key === name)!;
          group.get('value')?.reset();
          group.get('dataType')?.setValue(newField.dataType);
          group.get('customFieldType')?.setValue(newField.customFieldType!);
          group.get('validators')?.setValue(newField.customFieldValidators || null);
          group.get('allowedValues')?.setValue(newField.allowedValues || null);
          this.refreshSelection();
          if (this.shouldDisableGroup(group.get('customFieldType')?.value)) {
            group.get('value')?.disable();
          } else {
            group.get('value')?.enable();
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private refreshSelection() {
    const currentlySelected = this.form.value.map(({ name }) => name);
    this.allAvailableOptions.map(option => (option.selected = currentlySelected.includes(option.key)));
  }

  private initializeFormFields() {
    const isInternal$ = this.userService.getUserInfo().pipe(map(user => user.type === 'Internal'));
    return combineLatest([this.metadataService.getMetadata(), isInternal$]).pipe(
      tap(([{ shipmentColumns, containerColumns }, isInternal]) => {
        const filteredColumns = (this.openedFrom === 'Shipment' ? shipmentColumns : containerColumns).filter(
          ({ custom, customFieldType }) => custom && (isInternal || customFieldType !== 'Internal')
        );
        this.isUserInternal = isInternal;
        this.maxNumberOfFields = filteredColumns.length;
        this.formArrayInit(filteredColumns);
        this.setAllAvailableOptions(filteredColumns);
      })
    );
  }

  private shouldDisableGroup(customFieldType?: CustomFieldType) {
    return customFieldType === 'ExternalRead' && !this.isUserInternal;
  }
}
